/**
 * Per-ansatt BF-visning for profilsiden (VIB-632).
 *
 * Ren komposisjon av de kanoniske primitivene i dashboard-bf-calc — ALDRI en
 * konkurrerende formel. Målsemantikk låst av Erik 16.07 (VIB-1285, dokumentert
 * i docs/kpi-source-of-truth.md § BF-mål per selger): enhetsmålet er felles og
 * udelelig, oppnådd-status evalueres KUN på enhetsnivå, personens tall er et
 * BIDRAG (attribuert BF via ownership_pct) — aldri individuell måloppnåelse.
 *
 * No Supabase/React imports — deterministic and unit-tested.
 */
import {
  type DashboardProject,
  type DashboardOwner,
  type BfTargetSalesUnit,
  bfLevert,
  unitGoalStatus,
} from "./dashboard-bf-calc";
import type { DashboardPeriod } from "./dashboard-period";

export interface EmployeeBfNumbers {
  /** Er brukeren medlem av minst én salgsenhet for året? */
  hasUnit: boolean;
  /** Enhetens FULLE årsmål (kr) — deles aldri på medlemsantall. */
  targetKr: number;
  /** Enhetens samlede oppnåelse YTD: Σ attribuert BF over alle medlemmene. */
  unitLevertKr: number;
  /** Delt status: enhetens oppnåelse >= enhetsmål. Gjelder alle medlemmer likt. */
  achieved: boolean;
  /** Personens bidrag (attribuert BF) denne måneden. */
  monthBidragKr: number;
  /** Personens bidrag (attribuert BF) hittil i år. */
  ytdBidragKr: number;
}

/**
 * Beregner profilsidens BF-tall for én ansatt: personens bidrag (måned + YTD,
 * månedsattribusjon på completed_at — Erik 15.07) og enhetens delte
 * måloppnåelse via `unitGoalStatus`.
 */
export function calcEmployeeBf(input: {
  projects: DashboardProject[];
  owners: DashboardOwner[];
  salesUnits: BfTargetSalesUnit[];
  userId: string;
  year: number;
  /** 1–12 */
  month: number;
}): EmployeeBfNumbers {
  const { projects, owners, salesUnits, userId, year, month } = input;
  const yearPeriod: DashboardPeriod = { granularity: "year", year };
  const monthPeriod: DashboardPeriod = { granularity: "month", year, month };

  const hasUnit = salesUnits.some(
    (su) => su.year === year && su.memberUserIds.includes(userId),
  );
  const status = unitGoalStatus({ projects, owners, salesUnits, period: yearPeriod, userId });

  return {
    hasUnit,
    targetKr: status.targetKr,
    unitLevertKr: status.unitLevertKr,
    achieved: status.achieved,
    monthBidragKr: bfLevert(projects, owners, monthPeriod, { userId }),
    ytdBidragKr: bfLevert(projects, owners, yearPeriod, { userId }),
  };
}
